import type { ComponentMeta } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { getBlockName } from "../../Helpers/getBlockName";
import { MAX_TREE_DEPTH, PROPERTY_NAMES } from "../constants";
import type { ExtendedTreeItem, TreeItem } from "../types";
import { BlockOperations } from "./BlockOperations";
import { isChildrenProp, isObj } from "./utils";

const findAllChildrenWithNames = (
  blockProps: unknown,
  visited = new WeakSet<Record<string, unknown>>(),
): { propName: string; children: BaseBlock[] }[] => {
  const allChildrenGroups: { propName: string; children: BaseBlock[] }[] = [];
  if (!isObj(blockProps)) return allChildrenGroups;

  const queue: Record<string, unknown>[] = [blockProps];

  while (queue.length) {
    const current = queue.shift();
    if (!current) continue;
    if (visited.has(current)) continue;

    visited.add(current);

    for (const [key, prop] of Object.entries(current)) {
      if (isChildrenProp(prop)) {
        allChildrenGroups.push({
          propName: key,
          children: prop.value,
        });
        continue;
      }
      if (isObj(prop)) {
        queue.push(prop);
      }
    }
  }

  return allChildrenGroups;
};

const extractChildBlocks = (props: Record<string, unknown>): BaseBlock[] => {
  const childBlocks: BaseBlock[] = [];
  for (const prop of Object.values(props)) {
    if (isChildrenProp(prop)) {
      childBlocks.push(...prop.value);
    }
  }
  return childBlocks;
};

const createTreeItem = (
  block: BaseBlock,
  getComponentMeta: (blockType: string) => ComponentMeta | null,
): TreeItem => {
  const meta = getComponentMeta(block.type);

  const base: TreeItem = {
    id: block.id,
    type: block.type,
    collapsed: true,
    name: getBlockName({ block, meta }),
    isEditable:
      typeof block.meta?.isNameEditable === "boolean"
        ? block.meta.isNameEditable
        : true,
    isHidden: !!block.meta?.isHidden,
  };

  const childrenGroups = findAllChildrenWithNames(block.props);

  if (!childrenGroups.length) {
    base.canHaveChildren = false;
    return base;
  }

  base.children = childrenGroups.map(group => ({
    id: `${block.id}-${group.propName}`,
    type: group.propName,
    collapsed: true,
    children: TreeOperations.convertBlocksToTree(
      group.children,
      getComponentMeta,
    ),
    isPropertyNode: true,
    name: group.propName,
    isEditable: false,
  }));

  return base;
};

const areTreeItemsEqual = (a: TreeItem, b: TreeItem): boolean => {
  if (a === b) return true;
  if (a.id !== b.id || a.type !== b.type) return false;

  const aChildren = a.children ?? [];
  const bChildren = b.children ?? [];

  if (aChildren.length !== bChildren.length) return false;

  for (let i = 0; i < aChildren.length; i++) {
    if (!areTreeItemsEqual(aChildren[i], bChildren[i])) return false;
  }

  return true;
};

export const TreeOperations = {
  isPropertyNode: (item: TreeItem): boolean =>
    (item as ExtendedTreeItem).isPropertyNode === true,
  isObj,
  isChildrenProp,
  getActualBlockId: (itemId: string): string => {
    const lastDashIndex = itemId.lastIndexOf("-");
    if (lastDashIndex === -1) return itemId;

    const lastPart = itemId.substring(lastDashIndex + 1);
    return PROPERTY_NAMES.has(lastPart)
      ? itemId.substring(0, lastDashIndex)
      : itemId;
  },

  parsePropertyNodeId: (
    propertyNodeId: string,
  ): { parentBlockId: string; propName: string } | null => {
    const lastDashIndex = propertyNodeId.lastIndexOf("-");
    if (lastDashIndex === -1) return null;

    return {
      propName: propertyNodeId.substring(lastDashIndex + 1),
      parentBlockId: propertyNodeId.substring(0, lastDashIndex),
    };
  },

  convertBlocksToTree: (
    blocks: BaseBlock[],
    getComponentMeta: (blockType: string) => ComponentMeta | null,
  ): TreeItem[] => blocks.map(block => createTreeItem(block, getComponentMeta)),

  convertTreeToBlocks: (
    treeItems: TreeItem[],
    originalBlocks: BaseBlock[],
  ): BaseBlock[] => {
    const blockMap = BlockOperations.buildBlockMap(originalBlocks);
    const usedBlockIds = new Set<string>();

    const rebuildFromTree = (items: TreeItem[]): BaseBlock[] =>
      items
        .map(item =>
          BlockOperations.processBlock(
            item,
            blockMap,
            usedBlockIds,
            rebuildFromTree,
          ),
        )
        .filter((block): block is BaseBlock => block !== null);

    return rebuildFromTree(treeItems);
  },

  findParentItem: (
    items: TreeItem[],
    targetId: string,
    parent: TreeItem | null = null,
  ): TreeItem | null => {
    if (!items.length) return null;
    const stack = [{ items, parent, depth: 0 }];

    while (stack.length) {
      const popped = stack.pop();
      if (!popped) continue;
      const { items: currentItems, parent: currentParent, depth } = popped;

      if (depth > MAX_TREE_DEPTH) return null;

      for (const item of currentItems) {
        if (item.id === targetId) return currentParent;

        if (item.children?.length) {
          stack.push({ items: item.children, parent: item, depth: depth + 1 });
        }
      }
    }

    return null;
  },

  findParentIds: (items: TreeItem[], targetId: string): Set<string> => {
    const parents = new Set<string>();
    const stack = [{ items, parentIds: [] as string[] }];

    while (stack.length) {
      const popped = stack.pop();
      if (!popped) continue;
      const { items: currentItems, parentIds } = popped;

      for (const item of currentItems) {
        if (item.id === targetId) {
          return new Set(parentIds);
        }
        if (item.children?.length) {
          stack.push({
            items: item.children,
            parentIds: [...parentIds, item.id],
          });
        }
      }
    }

    return parents;
  },

  areTreesEqual: (a: TreeItem[], b: TreeItem[]): boolean => {
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      if (!areTreeItemsEqual(a[i], b[i])) return false;
    }
    return true;
  },

  updateCollapsedStateMap: (
    treeItems: TreeItem[],
    stateMap: Map<string, boolean>,
  ): void => {
    const stack = [...treeItems];
    while (stack.length) {
      const item = stack.pop();
      if (!item) continue;

      if (item.collapsed !== undefined) {
        stateMap.set(item.id, item.collapsed);
      }
      if (item.children?.length) {
        stack.push(...item.children);
      }
    }
  },

  applyCollapsedStateToTree: (
    treeItems: TreeItem[],
    stateMap: Map<string, boolean>,
    hasHiddenAncestor: boolean = false,
  ): TreeItem[] => {
    const result: TreeItem[] = [];
    const stack = [
      {
        items: treeItems,
        parent: result,
        hasHiddenAncestor,
      },
    ];

    while (stack.length) {
      const current = stack.pop();
      if (!current) continue;
      const { items, parent, hasHiddenAncestor } = current;

      for (const item of items) {
        const isItemHidden = item.isHidden || hasHiddenAncestor;
        const newItem: TreeItem = {
          ...item,
          collapsed: stateMap.get(item.id) ?? item.collapsed ?? true,
          hasHiddenAncestor: isItemHidden,
        };
        parent.push(newItem);

        if (item.children?.length) {
          newItem.children = [];
          stack.push({
            items: item.children,
            parent: newItem.children,
            hasHiddenAncestor: isItemHidden,
          });
        }
      }
    }
    return result;
  },

  shallowArrayEqual: <T>(arr1: T[] | null, arr2: T[]): boolean => {
    if (!arr1 || arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr2.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }
    return true;
  },

  findAllChildrenWithNames,
  extractChildBlocks,
} as const;
